import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Table } from 'react-bootstrap-5';
import PropTypes from 'prop-types';

import { get } from 'helpers/api';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { loadLlcData, saveLlcData } from '../../helpers/local_storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';

export default function Ownership({ owner, ownerOccupied, address, propertyId }) {
  const [loading, setLoading] = useState(false);
  const [llcData, setLlcData] = useState(null);
  const [lookupFailure, setLookupFailure] = useState(false);

  useEffect(() => {
    setLlcData(loadLlcData(propertyId));
  }, []);

  const lookupLlc = () => {
    if (llcData) return;

    setLoading(true);
    get(`/connect/off_market_properties/${propertyId}/llc_lookup`).then((data) => {
      setLoading(false);
      if (data) {
        setLlcData(data);
        saveLlcData(propertyId, data);
      } else {
        setLookupFailure(true);
      }
    }).catch(() => setLookupFailure(true));
  };

  const lookupButton = () => {
    return true; // Temporarily disabled

    if (llcData || !owner.isCorporation || lookupFailure) return;

    if (lookupFailure) return (
      <Button
        className="ms-3 text-danger"
        disabled
        size="sm"
        variant="link"
      >
        No LLC Data Found
      </Button>
    );

    return (
      <Button
        className="ms-3 text-secondary"
        disabled={loading}
        size="sm"
        variant="link"
        onClick={lookupLlc}
      >
        Get LLC Info
      </Button>
    );
  };

  const tableRow = (label, value, button = (<React.Fragment/>)) => {
    if (!value) return null;

    return (
      <tr>
        <td className="fw-bold">{label}</td>
        <td>
          {value}
          {button}
        </td>
      </tr>
    );
  };

  const llcPeople = [];
  if (llcData) {
    if (llcData.agentName) llcPeople.push({ title: 'Agent', name: llcData.agentName, address: llcData.agentAddress });
    (llcData.officers || []).forEach(({ position, name }) => llcPeople.push({ title: position, name }));
  }

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <FontAwesomeIcon className="text-secondary" icon={faCircleUser} />
        <h6 className="m-0 fw-bolder">Ownership</h6>
      </div>
      <Table>
        <tbody>
          {tableRow('Owner Name', owner.fullName, lookupButton())}
          {tableRow('Owner Type', (owner.isCorporation ? 'Company' : 'Private'))}
          {tableRow('Owner Occupies Property', (ownerOccupied ? 'Yes' : 'No'))}
          {tableRow('Owner Address', `${address.address}, ${address.city}, ${address.state} ${address.zip}`)}
        </tbody>
      </Table>
      {llcData && (
        <React.Fragment>
          <div className="bg-light p-3">
            <Row>
              <Col>
                <div><strong>{llcData.name}</strong></div>
                <small className="text-muted">{llcData.companyType && <div>{llcData.companyType}</div>}</small>
                {llcData.currentStatus && (
                  <div className="mt-1">
                    <small><strong>Status:</strong> {llcData.currentStatus}</small>
                  </div>
                )}
              </Col>
              <Col xs="auto">
                {llcData.incorporationDate && (
                  <small className="text-muted">Incorporated {formatDate(llcData.incorporationDate)}</small>
                )}
              </Col>
            </Row>
          </div>
          {llcPeople.length > 0 && (
            <Row className="pt-1 align-items-stretch g-1">
              {llcPeople.map((person, i) => (
                <Col key={i} lg="4" md="6">
                  <div className="bg-light p-3 h-100">
                    <div><strong>{person.name}</strong></div>
                    <div><small>{person.title}</small></div>
                    {person.address && <div><small>{person.address}</small></div>}
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </React.Fragment>
      )}
      {lookupFailure && (
        <Card>
          <Card.Header className="bg-light fw-bold">
            No LLC Match Found
          </Card.Header>
        </Card>
      )}
    </div>
  );
}

Ownership.propTypes = {
  address: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),
  owner: PropTypes.shape({
    fullName: PropTypes.string,
    isCorporation: PropTypes.bool
  }),
  ownerOccupied: PropTypes.bool,
  propertyId: PropTypes.number
};
